import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import {
  ETowerOption,
  IConstructionData
} from '../../../../store/ducks/contracts-and-constructions/types';
import { EConstructionRegistrationRoutes } from '../constructionRegistration.routes';
import {
  ContainerConstructionInfo,
  TowerOrBlockContainer,
  ActionsFooterContainer
} from './styles';
import {
  cnpjMask,
  uppercaseOnlyFirstLetter
} from '../../../../utils/formatStrings';
import { isAllFieldsValidInConstructionData } from '../constructionRegistrationFieldsValidations';
import TowerSelector from './TowerSelector';
import { ECustomerTypeView } from '../../../../store/ducks/user/types';
import { Title } from '@gerdau/hefesto/Title';
import { TextField } from '@gerdau/hefesto/TextField';
import { Skeleton } from '@gerdau/hefesto/Skeleton';
import { Button } from '@gerdau/hefesto/Button';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { useHistory } from 'react-router-dom';
import { ERoutes } from '../../../../routes';

interface IConstructionDataProps {
  goToConstructionRegistrationRoute(
    route: EConstructionRegistrationRoutes
  ): void;
  contractNumber: string;
  constructionData?: IConstructionData;
  setConstructionData(constructionData: IConstructionData): void;
}

const ConstructionData: React.FC<IConstructionDataProps> = ({
  goToConstructionRegistrationRoute,
  contractNumber,
  constructionData,
  setConstructionData
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const userState = useSelector((appState: ApplicationState) => appState.user);
  const customerContracts = useSelector(
    (appState: ApplicationState) =>
      appState.contractsAndConstructions.contractsManager.customerContracts
  );

  const contractsManagerState = useSelector(
    (appState: ApplicationState) =>
      appState.contractsAndConstructions.contractsManager
  );

  const initConstructionData = () => {
    const contract = customerContracts.find(
      (contr) => contr.contractNumber === contractNumber
    );

    if (contract && contract.shipTo && !constructionData) {
      const customerType = userState.searchCustomerType;
      const customerId =
        customerType === ECustomerTypeView.EMITTER
          ? contract.soldTo.code
          : contract.shipTo.code;
      setConstructionData({
        customerId: customerId,
        emitterCNPJ: contract.soldTo?.taxNumber || ' - ',
        receiverCNPJ: contract.shipTo?.taxNumber || ' - ',
        stateRegistration: contract.shipTo?.taxState || ' - ',
        constructionName: contract.customerOrder || '',
        address: {
          address: contract.shipTo.address || ' - ',
          state: contract.shipTo.state || ' - ',
          city: contract.shipTo.city || ' - ',
          cep: contract.shipTo.zipCode || ' - ',

          complement: '',
          towerOption: ETowerOption.UNIQUE,
          towers: []
        }
      });
    }
  };

  useEffect(() => {
    initConstructionData();
  });

  const goBackToContractsAndConstructons = () => {
    dispatch(
      sendTagging({
        event: 'click',
        screenName: 'LG:ContratosEObras:CadastroDeObra:DadosDaObra',
        itemName: 'button:voltar',
        itemDescription: 'contratosEObras_cadastroDeObra_dadosDaObra_voltar'
      })
    );
    history.push(ERoutes.CONTRACTS_AND_CONSTRUCTIONS);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const nextStep = () => {
    {
      goToConstructionRegistrationRoute(
        EConstructionRegistrationRoutes.DELIVERY_CONDITIONS
      );
      dispatch(
        sendTagging({
          event: 'click',
          screenName: 'LG:ContratosEObras:CadastroDeObra:DadosDaObra',
          itemName: 'button:confirmar_dados_da_obra',
          itemDescription:
            'contratosEObras_cadastroDeObra_dadosDaObra_confirmarDadosDaObra'
        })
      );
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onUpdateTowerSelector = (
    towerOption: ETowerOption,
    towers: string[]
  ) => {
    if (constructionData) {
      setConstructionData({
        ...constructionData,
        address: {
          ...constructionData.address,
          towerOption,
          towers
        }
      });
    }
  };

  return (
    <div>
      <ContainerConstructionInfo>
        <Title level={2}>Informações da obra</Title>
        <div>
          {contractsManagerState.loading ? (
            <Skeleton type="input" />
          ) : (
            <TextField
              label={'CNPJ da matriz (emissor)'}
              name={'emitter-cnpj'}
              readOnly
              value={cnpjMask(constructionData?.emitterCNPJ || '')}
            ></TextField>
          )}

          {contractsManagerState.loading ? (
            <Skeleton type="input" />
          ) : (
            <TextField
              label={'CNPJ da obra (SPE)'}
              name="spe-cnpj"
              readOnly
              value={cnpjMask(constructionData?.receiverCNPJ || '')}
            />
          )}

          {contractsManagerState.loading ? (
            <Skeleton type="input" />
          ) : (
            <TextField
              label={'Inscrição estadual da obra'}
              name="state-registration"
              readOnly
              value={constructionData?.stateRegistration}
            />
          )}

          {contractsManagerState.loading ? (
            <Skeleton type="input" />
          ) : (
            <TextField
              label={'Nome da obra'}
              name="construction-name"
              value={constructionData?.constructionName || ''}
              readOnly
            />
          )}
        </div>
      </ContainerConstructionInfo>

      <ContainerConstructionInfo>
        <Title level={2}>Endereço da obra</Title>

        <div className="construction-address">
          {contractsManagerState.loading ? (
            <Skeleton type="input" />
          ) : (
            <TextField
              label={'CEP da obra'}
              name="cep"
              readOnly
              value={constructionData?.address.cep}
            />
          )}
          {contractsManagerState.loading ? (
            <Skeleton type="input" />
          ) : (
            <TextField
              label={'Cidade'}
              name="city"
              readOnly
              value={uppercaseOnlyFirstLetter(
                constructionData?.address.city || ''
              )}
            />
          )}

          {contractsManagerState.loading ? (
            <Skeleton type="input" />
          ) : (
            <TextField
              label={'Estado'}
              name="state"
              readOnly
              value={constructionData?.address.state}
            />
          )}
        </div>
        <div className="construction-address">
          {contractsManagerState.loading ? (
            <Skeleton type="input" />
          ) : (
            <TextField
              label={'Endereço'}
              name="address"
              readOnly
              value={uppercaseOnlyFirstLetter(
                constructionData?.address.address || ''
              )}
            />
          )}
          <TextField
            name={'complement'}
            label={'Complemento'}
            placeholder="Opcional"
            disabled={!constructionData}
            value={constructionData?.address.complement}
            maxLength={150}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (constructionData) {
                setConstructionData({
                  ...constructionData,
                  address: {
                    ...constructionData.address,
                    complement: e.target.value
                  }
                });
              }
            }}
          />
        </div>
      </ContainerConstructionInfo>

      <TowerOrBlockContainer>
        <Title level={2}>Torre ou bloco</Title>
        <span>Selecione uma opção:</span>
        <TowerSelector
          towers={constructionData?.address.towers || []}
          towerOption={
            constructionData?.address.towerOption || ETowerOption.UNIQUE
          }
          onUpdateTowerSelector={onUpdateTowerSelector}
        />
      </TowerOrBlockContainer>

      <ActionsFooterContainer>
        <Button
          name={'backToContractsAndConstructions'}
          onClick={goBackToContractsAndConstructons}
          variant={'secondary'}
        >
          Voltar
        </Button>
        <Button
          name={'advanceToDelivery'}
          onClick={nextStep}
          disabled={
            !isAllFieldsValidInConstructionData(
              contractsManagerState,
              constructionData
            )
          }
        >
          Avançar para entrega
        </Button>
      </ActionsFooterContainer>
    </div>
  );
};

export default ConstructionData;
